/** * スケジュールコンポーネント */
<template>
  <div class="schedule">
    <div class="title-bar">
      <h1 v-t="'appSchedule.schedule'" />
      <div class="title-bar-action">
        <user-selector
          v-if="isManagerLoggingIn"
          single
          user-only
          all-organizations
          class="user-selector"
          v-bind:indoor-sites="$store.getters['indoorSite/indoorSites']"
          v-bind:primary-organization-id="
            $store.getters['userProfile/defaultOrganizationId']
          "
          v-bind:single-selected-title="$t('appSchedule.userToShowSchedule')"
          v-bind:value="[targetUser]"
          v-bind:keywordTarget="
            $store.getters['userKeywordTarget/keywordTargets']
          "
          v-bind:on-api-error="handleApiError"
          v-on:change:value="$_setUser"
          v-on:change:keywordTarget="
            $store.dispatch('userKeywordTarget/setKeywordTargets', $event)
          "
        />

        <!-- 作成ボタン -->
        <b-button
          variant="light"
          class="button-create"
          v-ifield-b-tooltip.hover
          v-bind:title="$t('appSchedule.action.register')"
          v-on:click="$_showEditor"
        >
          <icon-schedule class="icon" />
          <span class="label" v-t="'appSchedule.action.register'" />
        </b-button>
      </div>
    </div>
    <div class="control-container">
      <div class="control-block">
        <!-- 月選択コントロール -->
        <div class="month-control">
          <div class="month-arrow" v-on:click="$_onPreviousMonth()">
            <img class="arrow" src="@/assets/ifield/img_arrow_left.png" />
          </div>
          <div class="month-label">
            {{ displayCurrentMonth }}
          </div>
          <div class="month-arrow" v-on:click="$_onNextMonth()">
            <img class="arrow" src="@/assets/ifield/img_arrow_right.png" />
          </div>
        </div>
      </div>

      <div class="control-block">
        <!-- 絞り込みボタングループ -->
        <filter-button-group
          v-model="completedFilterState"
          v-bind:buttons="filterButtons"
          v-bind:label="$t('appSchedule.display')"
        />
        <!-- 担当外フィルタ -->
        <b-form-checkbox
          size="sm"
          class="include-non-assigned-schedule"
          v-model="includeNonAssignedSchedule"
        >
          <span class="label" v-t="'appSchedule.includeUnassignedSchedule'" />
        </b-form-checkbox>
      </div>
    </div>

    <schedule-calendar
      ref="schedule-calendar"
      class="schedule-calendar"
      v-bind:startDayOfWeek="startDayOfWeek"
      v-bind:canSelectUser="isManagerLoggingIn"
      v-bind:includeNonAssignedSchedule="includeNonAssignedSchedule"
      v-bind:completeFlg="completeFlg"
      v-bind:user="targetUser"
      v-on:schedule-date-selectend="$_onSelectTargetDate"
      v-on:change-view-month="$_onChangeMonth"
      v-on:calendar-onready="$_onChangeMonth"
    />
    <schedule-list
      class="schedule-list"
      v-bind:includeNonAssignedSchedule="includeNonAssignedSchedule"
      v-bind:completeFlg="completeFlg"
      v-bind:user="targetUser"
      v-bind:date="targetDate"
      v-on:schedule-changed="$_refreshCalendar"
    />
    <!-- スケジュール編集フォーム -->
    <schedule-editor ref="schedule-editor" />
  </div>
</template>

<script>
import scheduleCalendar from '@/components/schedule/AppScheduleCalendar.vue'
import scheduleList from '@/components/schedule/AppScheduleList.vue'
import filterButtonGroup from '@/components/AppFilterButtonGroup.vue'
import IconSchedule from '@/assets/ifield/ic_schedule.svg?component'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import ScheduleEditor from '@/views/TheScheduleEditor.vue'
import { DATE_FORMAT_HYPHEN } from '@/constants'
import moment from 'moment'

/**
 * 絞り込みフィルター名一覧
 */
const FILTER_NAME = {
  ALL: 'all',
  UNCOMPLETED: 'uncompleted',
  COMPLETED: 'completed',
}

export default {
  name: 'AppSchedule',
  components: {
    'schedule-calendar': scheduleCalendar,
    'schedule-list': scheduleList,
    'filter-button-group': filterButtonGroup,
    'icon-schedule': IconSchedule,
    'schedule-editor': ScheduleEditor,
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      // 現在表示中月
      displayCurrentMonth: null,
      // 完了フラグによる絞り込み状態
      completedFilterState: FILTER_NAME.ALL,
      // 担当以外を含める場合のフラグ
      includeNonAssignedSchedule: true,
      // 選択中ユーザー
      targetUser: null,
      // 選択中日付
      targetDate: moment().format(DATE_FORMAT_HYPHEN),
    }
  },
  computed: {
    /**
     * ログインユーザーが管理者権限を持っている場合はtrue
     * @type {Boolean}
     */
    isManagerLoggingIn: function () {
      return this.$store.state.login.isManager
    },
    /**
     * ユーザー選択の初期値
     * @type {Object}
     */
    initialValue: function () {
      const { userId, userName } = this.$store.getters.loginState
      return {
        type: 'user',
        id: userId,
        name: userName,
      }
    },
    /**
     * フィルターボタンラベルMap(key: フィルター名, value: 表示ラベル)
     * @return {Map}
     */
    filterLabelMap: function () {
      return new Map([
        [FILTER_NAME.ALL, this.$t('appSchedule.filter.all')],
        [FILTER_NAME.UNCOMPLETED, this.$t('appSchedule.filter.notYet')],
        [FILTER_NAME.COMPLETED, this.$t('appSchedule.filter.completed')],
      ])
    },
    /**
     * 絞り込みフィルター用ボタン定義
     * @type {Array.<Object>}
     */
    filterButtons: function () {
      return [
        {
          value: FILTER_NAME.ALL,
          text: this.filterLabelMap.get(FILTER_NAME.ALL),
        },
        {
          value: FILTER_NAME.UNCOMPLETED,
          text: this.filterLabelMap.get(FILTER_NAME.UNCOMPLETED),
        },
        {
          value: FILTER_NAME.COMPLETED,
          text: this.filterLabelMap.get(FILTER_NAME.COMPLETED),
        },
      ]
    },
    /**
     * カレンダー開始日付
     * @type {Number}
     */
    startDayOfWeek: function () {
      return this.$store.state.calendar.startDayOfWeek
    },
    /**
     * スケジュールの完了状態識別フラグ
     * @type {Boolean|Null}
     */
    completeFlg: function () {
      if (this.completedFilterState === FILTER_NAME.UNCOMPLETED) {
        return false
      } else if (this.completedFilterState === FILTER_NAME.COMPLETED) {
        return true
      }
      return null
    },
  },
  created: function () {
    this.$_updateIndoorSites()
    this.$_initializeTargetUser()
  },
  mounted: function () {
    this.$log.info('AppSchedule mounted.')
  },
  methods: {
    /**
     * サイトデータ更新
     */
    $_updateIndoorSites: async function () {
      try {
        this.$store.dispatch('indoorSite/updateIndoorSitesWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.info('failed to fetch indoor sites. use fetched before.')
        })
      }
    },

    /**
     * 対象ユーザー値を初期化する
     */
    $_initializeTargetUser: function () {
      // 承認権限がない場合はユーザー指定出来ないので空のままにする
      if (!this.isManagerLoggingIn) return

      const { userId, userName } = this.$store.getters.loginState
      this.targetUser = {
        type: 'user',
        id: userId,
        name: userName,
      }
    },

    /**
     * カレンダーの表示月変更時の処理
     * @param {String} currentMonth 表示する年月文字列
     */
    $_onChangeMonth: function (currentMonth) {
      this.displayCurrentMonth = currentMonth
    },
    /**
     * カレンダーを更新する
     */
    $_refreshCalendar: function () {
      this.$refs['schedule-calendar'].refreshCalendar()
    },
    /**
     * 前月への移動処理
     */
    $_onPreviousMonth: function () {
      this.$refs['schedule-calendar'].onPreviousMonth()
    },
    /**
     * 次月への移動処理
     */
    $_onNextMonth: function () {
      this.$refs['schedule-calendar'].onNextMonth()
    },
    /**
     * スケジュール表示対象対象ユーザーを設定
     * @param {Array.<Object>} user 対象ユーザー配列
     */
    $_setUser: function (users) {
      this.targetUser = users?.length ? users[0] : null
    },
    /**
     * スケジュール編集フォームを表示する
     */
    $_showEditor: function () {
      this.$refs['schedule-editor'].showModal()
    },
    /**
     * カレンダーの選択日付変更時処理
     * @param {String} date 開始日付
     */
    $_onSelectTargetDate: function (date) {
      this.targetDate = date
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../assets/scss/parts/title-bar';

$-title-bar-height: 3.2rem;
$-calendar-height: 40%;
$-calendar-min-height: 250px;

.schedule {
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  border: 1px solid #ebebeb;
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
}

.title-bar {
  display: flex;
  justify-content: space-between;
  flex-shrink: 0;
  height: $-title-bar-height;
  padding: 0 20px;

  h1 {
    font-size: $font-size-middle;
    letter-spacing: 0;
  }
}

.title-bar-action {
  justify-content: flex-end;
  column-gap: 12px;

  .button-create {
    @include mixin-button;
    & {
      height: $button-size;
      column-gap: 4px;
      padding: 8px;
    }

    .label {
      font-size: $font-size-small;
      font-weight: $font-weight-bold;
      color: $text-color-weak;
      margin-top: 1px;
    }
  }
}

.control-container {
  padding: 20px 20px 16px 20px;
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 16px;

  .control-block {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    column-gap: 8px;
    row-gap: 8px;
    flex-wrap: wrap;
  }
}

.month-control {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 8px;
  color: #959595;
  font-size: $font-size-middle;

  .month-arrow {
    flex-shrink: 0;
    display: block;
    width: 24px;
    height: 24px;
    margin: -8px;
    transition: 0.2s;

    &:hover {
      opacity: 0.75;
      cursor: pointer;
    }

    .arrow {
      display: block;
      width: 24px;
      height: 24px;
    }
  }

  .month-label {
    flex-shrink: 0;
    width: 5rem;
  }
}

::v-deep .filter-button-group {
  .filter-button {
    height: 30px;
    padding: 2px 8px;
  }
}

.include-non-assigned-schedule {
  color: $text-color;

  ::v-deep .custom-control-label {
    font-size: $font-size-small;

    .label {
      position: relative;
      top: 2px;
    }
  }
}

.schedule-calendar {
  height: calc(max(#{$-calendar-height}, #{$-calendar-min-height}));
  flex-shrink: 0;
  margin-bottom: 16px;
}

.schedule-list {
  overflow: hidden;
}
</style>
